import Vue from 'vue';
import VueRouter from 'vue-router';
import VueMeta from 'vue-meta';

import i18n from '../i18n';
import store from '../store';
import { globeStates } from '../store/globe';

const Onboarding = () => import('../views/Onboarding.vue');
const Home = () => import('../views/Home.vue');
const Questions = () => import('../views/Questions.vue');
const About = () => import('../views/About.vue');
const AboutOcean = () => import('../views/AboutOcean.vue');
const SelectLocation = () => import('../views/SelectLocation.vue');
const SectionOverview = () => import('../views/SectionOverview.vue');
const TimeoutOverview = () => import('../views/TimeoutOverview.vue');
const UsersJoined = () => import('../views/UsersJoined.vue');
const GlobalShock = () => import('../views/GlobalShock.vue');
const GlobalShockCountdown = () => import('../views/GlobalShockCountdown.vue');
const FocusCountdown = () => import('../views/FocusCountdown.vue');
const Dashboard = () => import('../views/Dashboard.vue');
const DashboardSummary = () => import('../views/DashboardSummary.vue');
const CompanyDetail = () => import('../views/CompanyDetail.vue');
const AddToGlobe = () => import('../views/AddToGlobe.vue');
const Summary = () => import('../views/Summary.vue');
const NotFound = () => import('../views/NotFound.vue');
const Upgrade = () => import('../views/Upgrade.vue');
const PersonalDashboard = () => import('../views/PersonalDashboard.vue');

Vue.use(VueRouter);
Vue.use(VueMeta, {
  keyName: 'head',
});

const allowedRoutesFlow = [
  ['onboarding'],
  ['home'],
  ['usersJoined'],
  ['location'],
  ['globalShock'],
  ['globalCountdown'],
  ['questions'],
  ['sectionOverview'],
  ['focusCountdown'],
  ['questions'],
  ['dashboardSummary'],
  ['dashboard'],
  ['addToGlobe'],
  ['companyDetail'],
  ['personalDashboard'],
  ['companyDetail'],
  ['summary'],
  ['aboutOcean', 'about'],
  ['summary'],
  ['onboarding'],

  // Dummy/Helper to break flow ("questions" is not allowed after "onboarding")
  ['-'],

  ['questions'],
  ['timeoutOverview'],
  ['home'],
];

const routes = [
  {
    path: '/',
    name: 'onboarding',
    component: Onboarding,
  },
  {
    path: '/home',
    name: 'home',
    component: Home,
  },

  {
    path: '/usersJoined',
    name: 'usersJoined',
    component: UsersJoined,
  },

  {
    path: '/location',
    name: 'location',
    component: SelectLocation,
  },

  {
    path: '/questions',
    name: 'questions',
    component: Questions,
  },

  {
    path: '/about',
    name: 'about',
    component: About,
  },

  {
    path: '/upgrade',
    name: 'upgrade',
    component: Upgrade,
  },

  {
    path: '/aboutOcean',
    name: 'aboutOcean',
    component: AboutOcean,
  },

  {
    path: '/globalShock',
    name: 'globalShock',
    component: GlobalShock,
  },

  {
    path: '/globalCountdown',
    name: 'globalCountdown',
    component: GlobalShockCountdown,
  },

  {
    path: '/focusCountdown',
    name: 'focusCountdown',
    component: FocusCountdown,
  },

  {
    path: '/sectionOverview',
    name: 'sectionOverview',
    component: SectionOverview,
  },

  {
    path: '/timeoutOverview',
    name: 'timeoutOverview',
    component: TimeoutOverview,
  },

  {
    path: '/addToGlobe',
    name: 'addToGlobe',
    component: AddToGlobe,
  },

  {
    path: '/companyDetail',
    name: 'companyDetail',
    component: CompanyDetail,
  },

  {
    path: '/summary',
    name: 'summary',
    component: Summary,
  },

  {
    path: '/dashboard',
    name: 'dashboard',
    component: Dashboard,
  },

  {
    path: '/dashboardSummary',
    name: 'dashboardSummary',
    component: DashboardSummary,
  },

  {
    path: '/personalDashboard',
    name: 'personalDashboard',
    component: PersonalDashboard,
  },

  {
    path: '*',
    name: 'notFound',
    component: NotFound,
  },
];

const router = new VueRouter({
  mode: 'hash', // neded for gitlab pages - check notion info
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  // Route target "onboarding" is always possible
  if (process.env.NODE_ENV === 'production' && from.name && to.name !== 'onboarding') {
    const allowed = allowedRoutesFlow.some(
      (value, index) =>
        value.includes(from.name) &&
        index + 1 < allowedRoutesFlow.length &&
        allowedRoutesFlow[index + 1].includes(to.name)
    );

    if (!allowed) return next({ name: 'onboarding' });
  }

  next();
});

router.beforeResolve((to, from, next) => {
  // TODO: unify analytics locations (also in public/index.html)
  const AnalyticsDataLayer = {
    events: { pageView: true },
    page: {
      url: `https://www.sap.com/dmc/exp/values-to-value#${to.name}`,
      section: 'dmc-demodern-ValuesToValue',
      language: 'en',
      country: 'glo',
    },
  };

  const customEvent = new CustomEvent('trackData', { detail: AnalyticsDataLayer });
  document.body.dispatchEvent(customEvent);

  if (!store.userModel) {
    // make sure we always have a user model even if we start somewhere in the app
    store.initUserModel();
  }

  if (
    !from.name || // initial loading
    (from.name && to.name === 'home') // reload for new game
  ) {
    // Reset user model and trivia (questions/sections)
    store.initUserModel();
    store.resetTrivia();

    // Load content and user data
    store.loadUserData();
    store.loadQuestionSections();
  }

  if (to.name === 'home') {
    store.setGlobeState({
      current: globeStates.INTRO,
      target: globeStates.LOCATION,
    });
  } else if (to.name === 'usersJoined') {
    store.setGlobeState(globeStates.USERS_JOINED);
  } else if (to.name === 'location') {
    store.setGlobeState(globeStates.LOCATION);
  } else if (to.name === 'onboarding') {
    store.setGlobeState(globeStates.INTRO);
  } else if (to.name === 'globalShock') {
    store.setGlobeState({
      current: globeStates.LOCATION,
      target: globeStates.SHOCK,
    });
  } else if (to.name === 'globalCountdown') {
    store.setGlobeState(globeStates.SHOCK);
  } else if (to.name === 'focusCountdown') {
    store.setGlobeState(globeStates.QUESTION);
  } else if (to.name === 'questions') {
    store.setGlobeState(globeStates.QUESTION);
  } else if (to.name === 'timeoutOverview') {
    store.setGlobeState(globeStates.RESULT);
    store.resetTrivia();
  } else if (to.name === 'sectionOverview') {
    store.setGlobeState(globeStates.QUESTION_AFTER_CHART);
  } else if (to.name === 'addToGlobe') {
    store.setGlobeState({
      current: globeStates.BEFORE_RESULT,
      target: globeStates.RESULT,
    });
  } else if (to.name === 'dashboard') {
    store.setGlobeState(globeStates.BEFORE_RESULT);
  } else if (to.name === 'dashboardSummary') {
    store.setGlobeState(globeStates.BEFORE_RESULT);
  } else if (to.name === 'companyDetail') {
    store.setGlobeState(globeStates.BROWSE_COMPANIES);
  } else if (to.name === 'personalDashboard') {
    store.setGlobeState(globeStates.BEFORE_RESULT);
  } else if (to.name === 'summary') {
    store.setGlobeState(globeStates.RESULT);
  } else if (to.name === 'about') {
    store.setGlobeState(globeStates.RESULT);
  } else if (to.name === 'aboutOcean') {
    store.setGlobeState(globeStates.RESULT);
  } else {
    // eslint-disable-next-line no-console
    console.log(
      `No globe state set for the route "${to.name}". Using "${globeStates.Intro}" as fallback`
    );
    store.setGlobeState(globeStates.INTRO);
  }

  next();
});

const DEBUG_BEFORE_UNLOAD = true;

const pagesAllowedToUnload = [
  'onboarding',
  'home',
  'summary',
  'about',
  'aboutOcean',
  'timeoutOverview',
];
window.addEventListener('beforeunload', event => {
  if (
    !DEBUG_BEFORE_UNLOAD ||
    process.env.NODE_ENV !== 'production' ||
    pagesAllowedToUnload.includes(router.currentRoute.name)
  ) {
    // the absence of a returnValue property on the event will guarantee the browser unload happens
    delete event.returnValue;
    return;
  }

  // Cancel the event as stated by the standard.
  event.preventDefault();
  // Chrome requires returnValue to be set.
  event.returnValue = i18n.t('ui.confirmLeave');
});

export default router;
